import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import { Card, CardHeader } from '@/components/atoms/Card';
import { Spinner } from '@/components/atoms/Spinner';
import { DataTable, type DataTableColumnDef } from '@/components/organisms/DataTable';
import {
    type ClientLicense as ClientLicenseRow,
    useGetClientLicenses,
    useGetLicenseCatalogue,
} from '@/domain/licenses';

import AddLicenseModal from './AddLicenseModal';
import DeleteLicenseModal from './DeleteLicenseModal';
import UpdateLicenseModal from './UpdateLicenseModal';

import styles from './ClientLicense.module.scss';

/**
 * Add License Id to show more license on Client Page
 */
const LICENSES = [
    {
        id: 2,
        name: 'Fish health minimal',
    },
    {
        id: 7,
        name: 'Lice Count data minimal',
    },
    {
        id: 8,
        name: 'Lice Count data maximum',
    },
    {
        id: 9,
        name: 'Fish Health Inspection',
    },
];

interface ClientLicenseProps {
    clientId: number;
}

function ClientLicense({ clientId }: ClientLicenseProps) {
    const { t } = useTranslation();
    const { data: clientLicnese, isLoading } = useGetClientLicenses(clientId);
    const { data: licenseList } = useGetLicenseCatalogue();

    const [modal, setModal] = useState<ReactNode>(null);

    const columns: DataTableColumnDef<ClientLicenseRow>[] = [
        {
            id: 'name',
            header: t('internal.client.licenseName'),
            cell: ({ row }) => row.original.name,
        },
        {
            id: 'remaining',
            header: t('internal.client.remaining'),
            cell: ({ row }) => (
                <>
                    {row.original?.left}/{row.original?.numLicenses}
                </>
            ),
        },
        {
            id: 'actions',
            header: t('common.labels.actions'),
            meta: { width: '260px' },
            cell: ({ row }) => (
                <div className={styles.actionButtonContainer}>
                    <Button
                        variant={'secondary'}
                        onClick={() => {
                            setModal(
                                <UpdateLicenseModal
                                    onClose={() => setModal(null)}
                                    selectedLicense={row.original}
                                />
                            );
                        }}>
                        {t('common.actions.edit')}
                    </Button>
                    <Button
                        variant={'danger'}
                        onClick={() => {
                            setModal(
                                <DeleteLicenseModal
                                    onClose={() => setModal(null)}
                                    selectedLicense={row.original}
                                />
                            );
                        }}>
                        {t('common.actions.deleteEntry')}
                    </Button>
                </div>
            ),
        },
    ];

    /**
     * Filtering out the remaining valid Licenses
     */
    const filterLicenseList = licenseList?.filter((license) =>
        LICENSES.some((L) => L.id === license.id)
    );

    return (
        <Card
            as="section"
            padding="xl"
            className={styles.licenseCard}
            header={<CardHeader title={t('internal.client.licenseDetail')} level="h2" />}>
            {isLoading ? (
                <Spinner size="lg" />
            ) : (
                <>
                    <DataTable
                        data={clientLicnese ?? []}
                        columns={columns}
                        enableSorting={false}
                        hover={false}
                    />
                    <Button
                        onClick={() => {
                            setModal(
                                <AddLicenseModal
                                    clientId={clientId}
                                    /**
                                     * @missingLicenses Licenses which Client dont have
                                     **/
                                    missingLicenses={(filterLicenseList ?? [])
                                        .filter(
                                            (l) =>
                                                !clientLicnese?.some((cl) => cl.licenseId === l.id)
                                        )
                                        .map((license) => license)}
                                    onClose={() => setModal(null)}
                                />
                            );
                        }}>
                        {t('common.actions.addLicense')}
                    </Button>
                    {modal ?? <></>}
                </>
            )}
        </Card>
    );
}

export default ClientLicense;
